import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  ButtonBase,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputBase,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { forwardRef, RefAttributes, ReactNode, ReactElement, useRef, useState } from 'react';
import { useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useFormFieldStyles } from './use-form-field-styles';
import * as HOOKS from '@siloreal:shared:hooks';

export type SelectorOption = string | { id: string; name: string };
export type SelectorGroupBy<T extends SelectorOption> = (option: T) => string;

type SelectorFieldSx = {
  flex: number;
  minWidth: string;
  mt?: string;
};

type BaseProps<T extends SelectorOption> = {
  tid: string;
  name: string;
  required?: boolean;
  label?: string;
  size?: 'medium' | 'large';
  multiple?: boolean;
  disabled?: boolean;
  renderOption?: (option: T) => ReactNode;
  groupBy?: SelectorGroupBy<T>;
  onChange?: (value: string | string[]) => void;
};

const GREEN = '#6BAA00';
const GREEN_DARK = '#4F8000';
const GREEN_SOFT = '#EFF8E7';
const GREEN_BORDER = '#CFE4B6';
const TEXT_COLOR = '#2F4319';
const DISABLED_TEXT = '#A8A8A8';
const DISABLED_BORDER = '#DADADA';
const DISABLED_BACKGROUND = '#F4F4F4';
const OPTION_HEIGHT = 44;
const OPTION_GAP = 4;
const MAX_VISIBLE_OPTIONS = 5;

type SelectorOptionGroup<T extends SelectorOption> = {
  name: string | null;
  options: T[];
};

function groupSelectorOptions<T extends SelectorOption>(
  options: T[],
  groupBy?: SelectorGroupBy<T>
): SelectorOptionGroup<T>[] {
  if (!groupBy) return [{ name: null, options }];

  const groups = new Map<string, T[]>();

  options.forEach((option) => {
    const groupName = groupBy(option);
    const groupOptions = groups.get(groupName);

    if (groupOptions) groupOptions.push(option);
    else groups.set(groupName, [option]);
  });

  return Array.from(groups, ([name, groupedOptions]) => ({ name, options: groupedOptions }));
}

const toSelectorValue = (value: unknown, multiple: boolean): string | string[] => {
  if (!multiple) return typeof value === 'string' ? value : '';
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
};

export type Props<T extends SelectorOption = SelectorOption> =
  | (BaseProps<T> & {
      options: T[];
      dependsOn?: never;
    })
  | (BaseProps<T> & {
      options: Record<string, T[]>;
      dependsOn: string;
    });

export const selector = forwardRef(
  <T extends SelectorOption = SelectorOption>(
    props: Props<T>,
    ref: RefAttributes<HTMLSelectElement>['ref']
  ) => {
    const { name = 'selector', tid } = props;
    const { label = 'Seleccionar opción', required = false } = props;
    const { options, dependsOn, renderOption, groupBy, onChange } = props;
    const { multiple = false, disabled = false } = props;
    const { size } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isFloatingOpen, setIsFloatingOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const summaryRef = useRef<HTMLDivElement | null>(null);

    const {
      register,
      setValue,
      formState: { errors },
      watch,
    } = useReactHookFormContext();
    const hasError = Boolean(errors[name]);
    const errorMessage = errors[name]?.message as string | undefined;

    const fieldSx = useFormFieldStyles({ size }) as SelectorFieldSx;

    const getOptionId = (option: T): string => {
      return typeof option === 'string' ? option : option.id;
    };

    const getDefaultOptionLabel = (option: T): string => {
      return typeof option === 'string' ? option : option.name;
    };

    const parentValue = dependsOn ? watch(dependsOn) : undefined;
    const currentValue = watch(name);
    const value = toSelectorValue(currentValue, multiple);

    const isArrayOptions = Array.isArray(options);
    const effectiveOptions = isArrayOptions
      ? (options as T[])
      : parentValue
        ? ((options as Record<string, T[]>)[parentValue] ?? [])
        : [];
    const getOptionLabel = (optionId: string): string => {
      const option = effectiveOptions.find((item) => getOptionId(item) === optionId);

      return option ? getDefaultOptionLabel(option) : optionId;
    };
    const selectedValues = Array.isArray(value) ? value : [];
    const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
    const hasSelection = selectedIds.length > 0;
    const hasOptions = effectiveOptions.length > 0;
    const hasOverflow = effectiveOptions.length > MAX_VISIBLE_OPTIONS;
    const isDisabled = disabled || Boolean(dependsOn && !parentValue) || !hasOptions;
    const isExpanded = isOpen || isFloatingOpen;
    const showingSelectedOptions = showSelectedOnly && hasSelection;
    const normalizedSearch = search.trim().toLocaleLowerCase('es');
    const visibleOptions = effectiveOptions.filter((option) => {
      const optionId = getOptionId(option);
      const matchesSelection = !showingSelectedOptions || selectedIds.includes(optionId);
      const matchesSearch =
        !normalizedSearch || getDefaultOptionLabel(option).toLocaleLowerCase('es').includes(normalizedSearch);

      return matchesSelection && matchesSearch;
    });
    const visibleOptionGroups = groupSelectorOptions(visibleOptions, groupBy);

    const registeredField = register(name, { required });
    const { ref: registeredRef, ...fieldRegistration } = registeredField;

    const updateValue = (nextValue: string | string[]): void => {
      setValue(name, nextValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      onChange?.(nextValue);
    };

    const toggleOption = (optionId: string): void => {
      if (!multiple) {
        updateValue(optionId);
        setIsOpen(false);
        setIsFloatingOpen(false);
        return;
      }

      const nextValues = selectedValues.includes(optionId)
        ? selectedValues.filter((selectedId) => selectedId !== optionId)
        : [...selectedValues, optionId];

      if (nextValues.length === 0 || selectedValues.length === 0) setShowSelectedOnly(false);
      updateValue(nextValues);
    };

    const clearValue = (): void => {
      updateValue(multiple ? [] : '');
      setShowSelectedOnly(false);
    };

    const summary = (() => {
      if (!hasOptions) return 'No hay opciones disponibles';
      if (!hasSelection) return '—';

      const labels = selectedIds.map(getOptionLabel);
      if (labels.length <= 2) return labels.join(', ');
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
    })();

    const assignRef = (node: HTMLSelectElement | null): void => {
      registeredRef(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const i18n = HOOKS.useLocale();

    const renderOptionsPanel = (floating = false): ReactNode => (
      <Box
        id={`${tid}-options${floating ? '-floating' : ''}`}
        sx={{
          width: floating ? 'min(420px, calc(100vw - 32px))' : '100%',
          minWidth: 0,
          border: '1px solid',
          borderTop: floating ? '1px solid' : 0,
          borderColor: hasError ? 'error.main' : '#E2EAD9',
          borderRadius: floating ? '8px' : '0 0 8px 8px',
          backgroundColor: '#FFFFFF',
          boxShadow: floating ? '0 14px 36px rgba(47, 67, 25, 0.16)' : 'none',
          overflow: 'hidden',
        }}
      >
        {hasOverflow && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: 0.75,
              borderBottom: '1px solid #E8EFE2',
              backgroundColor: '#FAFCF8',
            }}
          >
            <InputBase
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={i18n.t('temp.common.search_option')}
              inputProps={{ 'aria-label': `Buscar en ${label}` }}
              startAdornment={
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <SearchRoundedIcon sx={{ fontSize: 17, color: GREEN_DARK }} />
                </InputAdornment>
              }
              sx={{
                flex: 1,
                minWidth: 0,
                height: 30,
                px: 0.75,
                border: '1px solid #DCE8D2',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF',
                fontSize: 13,
                '&.Mui-focused': {
                  borderColor: GREEN,
                  boxShadow: '0 0 0 2px rgba(107, 170, 0, 0.12)',
                },
              }}
            />

            <Tooltip title={i18n.t('temp.common.see_active_options')}>
              <span>
                <ButtonBase
                  component="button"
                  type="button"
                  disabled={!hasSelection}
                  aria-label="Ver opciones activas"
                  aria-pressed={showingSelectedOptions}
                  onClick={() => setShowSelectedOnly((current) => !current)}
                  sx={{
                    height: 30,
                    gap: 0.4,
                    px: 0.75,
                    border: '1px solid',
                    borderColor: showingSelectedOptions ? GREEN : '#DCE8D2',
                    borderRadius: '6px',
                    color: showingSelectedOptions ? GREEN_DARK : '#66735B',
                    backgroundColor: showingSelectedOptions ? GREEN_SOFT : '#FFFFFF',
                    fontSize: 12,
                    fontWeight: 700,
                    '&:hover': { backgroundColor: GREEN_SOFT },
                    '&.Mui-disabled': { opacity: 0.45 },
                  }}
                >
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />
                  {i18n.t('temp.common.actives')}
                </ButtonBase>
              </span>
            </Tooltip>

            <Tooltip
              title={floating ? i18n.t('temp.common.return_to_selector') : i18n.t('temp.common.expand_list')}
            >
              <IconButton
                type="button"
                size="small"
                aria-label={floating ? 'Volver al selector' : 'Ampliar lista'}
                onClick={() => {
                  setIsFloatingOpen(!floating);
                  setIsOpen(floating);
                }}
                sx={{
                  width: 30,
                  height: 30,
                  border: '1px solid #DCE8D2',
                  borderRadius: '6px',
                  color: GREEN_DARK,
                  backgroundColor: floating ? GREEN_SOFT : '#FFFFFF',
                  '&:hover': { backgroundColor: GREEN_SOFT },
                }}
              >
                <OpenInNewRoundedIcon
                  sx={{ fontSize: 16, transform: floating ? 'rotate(180deg)' : 'none' }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box
          role="listbox"
          aria-multiselectable={multiple || undefined}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${OPTION_GAP}px`,
            maxHeight: floating
              ? 'calc(100vh - 96px)'
              : hasOverflow
                ? OPTION_HEIGHT * MAX_VISIBLE_OPTIONS + OPTION_GAP * (MAX_VISIBLE_OPTIONS - 1) + 12
                : 'none',
            overflowY: floating || hasOverflow ? 'auto' : 'hidden',
            p: 0.75,
            scrollbarWidth: 'thin',
            scrollbarColor: `${GREEN_BORDER} transparent`,
          }}
        >
          {visibleOptions.length === 0 ? (
            <Typography variant="body2" sx={{ px: 1.25, py: 1.5, color: '#757575' }}>
              {showingSelectedOptions ? 'No hay opciones activas' : 'No se encontraron opciones'}
            </Typography>
          ) : (
            visibleOptionGroups.map((group, groupIndex) => {
              const groupLabelId = `${tid}-group-${groupIndex}${floating ? '-floating' : ''}`;

              return (
                <Box
                  key={group.name ?? 'options'}
                  role={group.name ? 'group' : undefined}
                  aria-labelledby={group.name ? groupLabelId : undefined}
                  sx={{ display: 'flex', flexDirection: 'column', gap: `${OPTION_GAP}px` }}
                >
                  {group.name ? (
                    <Typography
                      id={groupLabelId}
                      variant="caption"
                      sx={{
                        px: 1.25,
                        pt: groupIndex === 0 ? 0.5 : 1,
                        pb: 0.25,
                        color: '#66735B',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        lineHeight: 1.4,
                        textTransform: 'uppercase',
                      }}
                    >
                      {group.name}
                    </Typography>
                  ) : null}

                  {group.options.map((option) => {
                    const optionId = getOptionId(option);
                    const selected = selectedIds.includes(optionId);

                    return (
                      <ButtonBase
                        key={optionId}
                        component="button"
                        type="button"
                        role="option"
                        aria-selected={selected}
                        data-testid={`${tid}-option-${optionId}`}
                        onClick={() => toggleOption(optionId)}
                        sx={{
                          width: '100%',
                          minHeight: OPTION_HEIGHT,
                          justifyContent: 'flex-start',
                          gap: 1,
                          px: 1.25,
                          py: 0.75,
                          border: '1px solid',
                          borderColor: selected ? GREEN_BORDER : 'transparent',
                          borderRadius: '6px',
                          color: selected ? GREEN_DARK : '#4F4A53',
                          backgroundColor: selected ? GREEN_SOFT : 'transparent',
                          textAlign: 'left',
                          transition:
                            'background-color 140ms ease, border-color 140ms ease, color 140ms ease',
                          '&:hover': {
                            borderColor: selected ? GREEN : GREEN_BORDER,
                            backgroundColor: GREEN_SOFT,
                          },
                          '&.Mui-focusVisible': {
                            boxShadow: `inset 0 0 0 2px ${GREEN}`,
                          },
                        }}
                      >
                        <Box
                          aria-hidden
                          sx={{
                            width: 20,
                            height: 20,
                            flex: '0 0 20px',
                            display: 'grid',
                            placeItems: 'center',
                            border: '1px solid',
                            borderColor: selected ? GREEN : '#C7C7C7',
                            borderRadius: multiple ? '5px' : '50%',
                            backgroundColor: selected ? GREEN : '#FFFFFF',
                            color: '#FFFFFF',
                          }}
                        >
                          {selected ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : null}
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500 }}>
                          {renderOption?.(option) ?? getDefaultOptionLabel(option)}
                        </Typography>
                      </ButtonBase>
                    );
                  })}
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    );

    return (
      <FormControl
        sx={{
          ...fieldSx,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'grid',
          gap: 0,
        }}
        error={hasError}
        required={required}
        disabled={isDisabled}
      >
        <select
          {...fieldRegistration}
          ref={assignRef}
          value={value}
          multiple={multiple}
          tabIndex={-1}
          aria-hidden
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {!multiple ? <option value="" /> : null}
          {effectiveOptions.map((option) => (
            <option key={getOptionId(option)} value={getOptionId(option)} />
          ))}
        </select>

        <Box
          ref={summaryRef}
          data-testid={tid}
          sx={{
            width: '100%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            border: '1px solid',
            borderColor: hasError
              ? 'error.main'
              : isDisabled
                ? DISABLED_BORDER
                : isExpanded || hasSelection
                  ? GREEN
                  : GREEN_BORDER,
            borderRadius: isOpen ? '8px 8px 0 0' : '8px',
            backgroundColor: isDisabled ? DISABLED_BACKGROUND : isExpanded ? GREEN_SOFT : '#FFFFFF',
            color: isDisabled ? DISABLED_TEXT : TEXT_COLOR,
            overflow: 'hidden',
            transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
            '&:hover': isDisabled
              ? undefined
              : {
                  borderColor: GREEN,
                  backgroundColor: GREEN_SOFT,
                },
          }}
        >
          <ButtonBase
            component="button"
            type="button"
            disabled={isDisabled}
            data-testid={`${tid}-summary-btn`}
            aria-label={`Mostrar opciones de ${label}`}
            aria-expanded={isExpanded}
            aria-controls={`${tid}-options`}
            onClick={() => {
              setIsFloatingOpen(false);
              setIsOpen((current) => !current);
            }}
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 1.25,
              py: 0.9,
              color: 'inherit',
              textAlign: 'left',
              '&.Mui-focusVisible': {
                boxShadow: 'inset 0 0 0 3px rgba(107, 170, 0, 0.22)',
              },
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <FormatListBulletedRoundedIcon
                sx={{ fontSize: 20, color: isDisabled ? DISABLED_TEXT : GREEN, flex: '0 0 auto' }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: isDisabled ? DISABLED_TEXT : '#66735B', lineHeight: 1.2 }}
                >
                  {label}
                  {required && (
                    <Box component="span" sx={{ color: '#EA1818', ml: 0.25 }}>
                      *
                    </Box>
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'block',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 700,
                    color: isDisabled ? DISABLED_TEXT : hasSelection ? TEXT_COLOR : '#5C6A50',
                  }}
                >
                  {summary}
                </Typography>
              </Box>
            </Box>

            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 22,
                color: isDisabled ? DISABLED_TEXT : GREEN,
                flex: '0 0 auto',
                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 180ms ease',
              }}
            />
          </ButtonBase>

          {hasSelection && !isDisabled && (
            <IconButton
              type="button"
              size="small"
              aria-label={`Borrar selección de ${label}`}
              data-testid={`${tid}-clear-btn`}
              onClick={(event) => {
                event.stopPropagation();
                clearValue();
              }}
              sx={{
                alignSelf: 'stretch',
                flex: '0 0 42px',
                width: 42,
                minWidth: 42,
                height: 'auto',
                p: 0,
                borderLeft: '1px solid #F7C2C2',
                borderRadius: 0,
                color: '#EA1818',
                backgroundColor: '#FDE8E8',
                '&:hover': {
                  backgroundColor: '#FDE8E8',
                  boxShadow: '0 0 0 3px rgba(234, 24, 24, 0.12)',
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: isOpen ? '1fr' : '0fr',
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
            transition: 'grid-template-rows 180ms ease, opacity 180ms ease, transform 180ms ease',
          }}
        >
          <Box sx={{ overflow: 'hidden', minWidth: 0 }}>{renderOptionsPanel()}</Box>
        </Box>

        <Popover
          open={isFloatingOpen}
          anchorEl={summaryRef.current}
          onClose={() => setIsFloatingOpen(false)}
          disableScrollLock
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                ml: 1,
                borderRadius: '8px',
                overflow: 'visible',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              },
            },
          }}
        >
          {renderOptionsPanel(true)}
        </Popover>

        <FormHelperText>{errorMessage}</FormHelperText>
      </FormControl>
    );
  }
) as <T extends SelectorOption = SelectorOption>(
  props: Props<T> & { ref?: RefAttributes<HTMLSelectElement>['ref'] }
) => ReactElement;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(selector as any).displayName = 'SiloReal-Form-Field-Selector';
